import { FC, MouseEvent, useState } from 'react';
import { ScanCompareFullScreen } from '../components/ScanCompare/FullScreen';
import { MeshViewerTable } from './Table/MeshViewerTable';
import {
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
} from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useLayerStore } from './LayerControls/layerStore';
import { useExport3DModel } from '../hooks';
import { useAccountContext } from '../../account';

interface SideControlsProps {
  fullScreenContainerRef: React.RefObject<HTMLDivElement>;
}

export const SideControls: FC<SideControlsProps> = ({
  fullScreenContainerRef,
}) => {
  const { user } = useAccountContext();
  const { modelType: type } = useLayerStore();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { exportModel, isExporting } = useExport3DModel();

  const export3DModelsEnabled = user.export_3d_models_enabled;

  const handleExportClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (fileType: 'ply' | 'stl') => {
    handleExportClose();
    await exportModel({
      model_type: type,
      file_type: fileType,
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'flex-end',
        textAlign: 'right',
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '8px',
          flexDirection: open ? 'row' : 'column',
          width: '100%',
          height: '48px',
        }}
      >
        <ScanCompareFullScreen containerRef={fullScreenContainerRef} />
        <Tooltip
          title={open ? 'close table' : 'open table'}
          placement='top'
          disableInteractive
        >
          <IconButton
            onClick={() => setOpen(!open)}
            sx={{
              padding: '12px',
              borderRadius: '8px',
              color: '#353535',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {open ? (
              <TableChartIcon fontSize='inherit' />
            ) : (
              <TableChartOutlinedIcon fontSize='inherit' />
            )}
          </IconButton>
        </Tooltip>
        {export3DModelsEnabled && (
          <>
            <Tooltip title='export 3D model' placement='top' disableInteractive>
              <IconButton
                onClick={handleExportClick}
                disabled={isExporting}
                sx={{
                  padding: '12px',
                  borderRadius: '8px',
                  color: '#353535',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <FileDownloadOutlinedIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleExportClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <MenuList>
                <MenuItem onClick={() => handleExport('ply')}>PLY</MenuItem>
                <MenuItem onClick={() => handleExport('stl')}>STL</MenuItem>
              </MenuList>
            </Popover>
          </>
        )}
      </div>
      <MeshViewerTable open={open} />
    </div>
  );
};
